import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { cnb } from "cnbuilder";
import { v4 as uuidv4 } from "uuid";
import ReactSlider from "react-slider";

// Styles are defined in modernized-rangeslider.module.css

const RangeSlider = ({
  classNameRoot,
  classNameCurrentValue,
  classNameContainer,
  classNameTrack,
  classNameHandle,
  classNameMinMaxLabels,
  classNameMinLabel,
  classNameMaxLabel,
  testAttrRoot,
  testAttrCurrentValue,
  testAttrMinMaxLabels,
  testAttrMinLabel,
  testAttrMaxLabel,
  i18nHandleLabels,
  i18nMinLabel,
  i18nMaxLabel,
  min,
  max,
  step,
  defaultValue,
  showMinMaxLabels,
  showCurrentValueLabel,
  renderCurrentValue,
  onChange,
  value,
  disabled
}) => {
  const { current: uuid } = useRef(uuidv4());
  const [stateValue, setStateValue] = useState(
    value !== null ? value : defaultValue
  );
  const labelId = `label-${uuid}`;

  const onChanged = val => {
    setStateValue(val);

    if (onChange) {
      onChange(val);
    }
  };

  return (
    <div
      className={cnb("modernizedRangeSlider", classNameRoot)}
      data-testid={testAttrRoot}
    >
      {showCurrentValueLabel && (
        <div
          id={labelId}
          className={cnb(
            "modernizedRangeSlider-currentValue",
            classNameCurrentValue
          )}
          data-testid={testAttrCurrentValue}
        >
          {renderCurrentValue(value !== null ? value : stateValue)}
        </div>
      )}
      <ReactSlider
        className={cnb("modernizedRangeSlider-container", classNameContainer)}
        thumbClassName={cnb("modernizedRangeSlider-handle", classNameHandle)}
        trackClassName={cnb(classNameTrack, "modernizedRangeSlider-track")}
        min={min}
        max={max}
        step={step}
        ariaLabel={i18nHandleLabels}
        {...(showCurrentValueLabel && { ariaLabelledby: labelId })}
        onChange={onChanged}
        value={value !== null ? value : stateValue}
        disabled={disabled}
      />
      {showMinMaxLabels && (
        <div
          className={cnb("modernizedRangeSlider-labels", classNameMinMaxLabels)}
          data-testid={testAttrMinMaxLabels}
        >
          <div className={classNameMinLabel} data-testid={testAttrMinLabel}>
            {i18nMinLabel || min}
          </div>
          <div className={classNameMaxLabel} data-testid={testAttrMaxLabel}>
            {i18nMaxLabel || max}
          </div>
        </div>
      )}
    </div>
  );
};

RangeSlider.propTypes = {
  /** Additional class(es) to add to the root element. */
  classNameRoot: PropTypes.string,
  /** Additional class(es) to add to the current value element. */
  classNameCurrentValue: PropTypes.string,
  /** Additional class(es) to add to the slider container element. */
  classNameContainer: PropTypes.string,
  /** Additional class(es) to add to the track element. */
  classNameTrack: PropTypes.string,
  /** Additional class(es) to add to the handle element. */
  classNameHandle: PropTypes.string,
  /** Additional class(es) to add to the min/max label element. */
  classNameMinMaxLabels: PropTypes.string,
  /** Additional class(es) to add to the min label element. */
  classNameMinLabel: PropTypes.string,
  /** Additional class(es) to add to the max label element. */
  classNameMaxLabel: PropTypes.string,
  /** The name of the test attribute to associate to the component's root element. */
  testAttrRoot: PropTypes.string,
  /** The name of the test attribute to associate to the component's current value element. */
  testAttrCurrentValue: PropTypes.string,
  /** The name of the test attribute to associate to the component's min/max label element. */
  testAttrMinMaxLabels: PropTypes.string,
  /** The name of the test attribute to associate to the component's min label element. */
  testAttrMinLabel: PropTypes.string,
  /** The name of the test attribute to associate to the component's max label element. */
  testAttrMaxLabel: PropTypes.string,
  /** The aria-label to apply to the handle(s). */
  i18nHandleLabels: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  /** The formatted label text for the maximum value end of the slider (defaults to min value if not passed). */
  i18nMaxLabel: PropTypes.string,
  /** The formatted label text for the minimum value end of the slider (defaults to max value if not passed). */
  i18nMinLabel: PropTypes.string,
  /** The minimum value of the track. */
  min: PropTypes.number,
  /** The maximum value of the track. */
  max: PropTypes.number,
  /** Whether to show the min and max labels. */
  showMinMaxLabels: PropTypes.bool,
  /** Whether to show the min and max labels. */
  showCurrentValueLabel: PropTypes.bool,
  /**
   * A custom render method for displaying the current value above the slider.
   *
   * @param {Number[]} values - The value(s) of the slider handle position(s).
   */
  renderCurrentValue: PropTypes.func,
  /** The value to be added/subtracted on each step the handle takes. */
  step: PropTypes.number,
  /** The default value of the slider handle. */
  defaultValue: PropTypes.number,
  /**
   * Function to execute when the value(s) of the slider(s) is/are changing during dragging operations.
   *
   * @param {Number[]} values - The value(s) of the slider handle position(s).
   */
  onChange: PropTypes.func,
  /**
   * The value(s) of the slider handle(s) when used as a controlled component.
   */
  value: PropTypes.number,
  /**
   * If `true` the thumbs can't be moved.
   *
   * default to false
   */
  disabled: PropTypes.bool
};

RangeSlider.defaultProps = {
  testAttrRoot: "range-slider-root",
  testAttrCurrentValue: "range-slider-current-value",
  testAttrMinMaxLabels: "range-slider-min-max-labels",
  testAttrMinLabel: "range-slider-min-label",
  testAttrMaxLabel: "range-slider-max-label",
  min: 0,
  max: 100,
  step: 1,
  value: null,
  defaultValue: 50,
  showMinMaxLabels: true,
  showCurrentValueLabel: true,
  disabled: false,
  renderCurrentValue: value => value
};

export default RangeSlider;
